import { useReducedMotion } from 'framer-motion';
import type { Transition, Variants } from 'framer-motion';

/**
 * The tour's motion vocabulary, in one place. Springs, not eases: things
 * arrive with weight and settle. Every preset collapses to an instant swap
 * under prefers-reduced-motion via useTourMotion().
 */
export const spring: Transition = { type: 'spring', stiffness: 260, damping: 28, mass: 0.9 };
export const softSpring: Transition = { type: 'spring', stiffness: 140, damping: 22, mass: 1 };

export const reveal: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05, delayChildren: 0.02 } }
};

export const rise: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: spring }
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  show: { opacity: 1, scale: 1, transition: softSpring }
};

export const pop: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  show: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 380, damping: 22 } }
};

/** Draws an SVG path over `dur` seconds (uses framer's pathLength). */
export const draw = (dur = 0.9, delay = 0): Variants => ({
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: { pathLength: { duration: dur, delay, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.2, delay } }
  }
});

const instant: Variants = { hidden: { opacity: 1 }, show: { opacity: 1 } };

/** Returns variants unchanged, or instant ones when the OS asks for less motion. */
export const useTourMotion = () => {
  const reduce = useReducedMotion();
  const v = <T extends Variants>(variants: T): Variants => (reduce ? instant : variants);
  return { reduce, v };
};
